import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_FACTORY_ABI } from '../abis/ChallengeFactoryABI'
import { CHALLENGE_TYPE } from '../types/challenge'

export function useIsVerifiedCreator(address: string | null) {
  return useQuery<boolean>({
    queryKey: ['isVerifiedCreator', address],
    queryFn: async () => {
      const factoryAddress = import.meta.env.VITE_FACTORY_ADDRESS as string | undefined
      if (!factoryAddress || !address) return false
      const provider = getReadProvider()
      const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, provider)
      return (factory.isVerifiedCreator as (a: string) => Promise<boolean>)(address)
    },
    enabled: Boolean(address),
    staleTime: 60_000,
  })
}

export interface CreateChallengeParams {
  challengeType: number
  name: string
  startTime: number
  endTime: number
  minParticipants: number
  maxParticipants: number
  entryFee: bigint
  winnerCount: number
  roundDeadlines: number[]
  prizeEth: bigint
  description: string
  emoji: string
  color: string
  proofType: string
}

export function parseCreateError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('user rejected') || msg.includes('ACTION_REJECTED'))
      return '트랜잭션을 거부했습니다'
    if (msg.includes('insufficient funds')) return '잔액이 부족합니다'
    return msg.slice(0, 120)
  }
  return '개설에 실패했습니다'
}

interface ChallengeParamsStruct {
  challengeType: number
  name: string
  startTime: number
  endTime: number
  roundDeadlines: number[]
  minParticipants: number
  maxParticipants: number
  entryFee: bigint
  winnerCount: number
}

type CreateFn = (
  params: ChallengeParamsStruct,
  opts: { value: bigint },
) => Promise<ethers.TransactionResponse>

export function useCreateChallenge() {
  const queryClient = useQueryClient()

  return useMutation<string, Error, CreateChallengeParams>({
    mutationFn: async (params) => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')
      const factoryAddress = import.meta.env.VITE_FACTORY_ADDRESS as string | undefined
      if (!factoryAddress) throw new Error('Factory 컨트랙트 주소가 설정되지 않았습니다')

      const browserProvider = new ethers.BrowserProvider(window.ethereum)
      const signer = await browserProvider.getSigner()
      const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, signer)
      // ENTRY_FEE: the creator pays the entry fee at creation and is auto-joined atomically
      // by the contract. PROMO: the value is the prize the creator deposits.
      const msgValue =
        params.challengeType === CHALLENGE_TYPE.ENTRY_FEE ? params.entryFee : params.prizeEth

      const tx = await (factory.createChallenge as CreateFn)(
        {
          challengeType: params.challengeType,
          name: params.name,
          startTime: params.startTime,
          endTime: params.endTime,
          roundDeadlines: params.roundDeadlines,
          minParticipants: params.minParticipants,
          maxParticipants: params.maxParticipants,
          entryFee: params.entryFee,
          winnerCount: params.winnerCount,
        },
        { value: msgValue },
      )

      const receipt = await tx.wait()
      let cloneAddress = ''

      if (receipt) {
        for (const log of receipt.logs) {
          try {
            const parsed = factory.interface.parseLog({
              topics: log.topics as string[],
              data: log.data,
            })
            if (parsed?.name === 'ChallengeCreated') {
              cloneAddress = parsed.args.clone as string
              break
            }
          } catch {
            // ignore unparseable logs
          }
        }
      }

      if (!cloneAddress) throw new Error('챌린지 주소를 찾을 수 없습니다')

      if (params.description.trim()) {
        const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
        try {
          await fetch(`${apiBase}/api/challenges`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contractAddress: cloneAddress,
              title: params.name,
              description: params.description,
              emoji: params.emoji,
              color: params.color,
              proofType: params.proofType,
            }),
          })
        } catch {
          // Non-fatal: off-chain description save failed
        }
      }

      return cloneAddress
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['challenges'] })
    },
  })
}
